import React, { Component } from "react";
import CartContext from "../StoreManager/CartContext";
import { Table, Spinner, Alert, Button } from "react-bootstrap";

export default class HotelBookingCalendar extends Component {
  static contextType = CartContext;

  constructor(props) {
    super(props);
    this.state = {
      from: this.getMonday(new Date()),
      days: 7,
      rooms: [],
      entries: [],
      loading: false,
      error: null,
      dragItem: null,
      occupancyStats: null,
    };
  }

  // Compute Monday as week start (non-mutating)
  getMonday(d) {
    const date = new Date(d);
    const day = date.getDay();
    const diff = date.getDate() - day + (day === 0 ? -6 : 1);
    const monday = new Date(date);
    monday.setDate(diff);
    monday.setHours(0, 0, 0, 0);
    return monday;
  }

  fmtDate = (d) => {
    const date = typeof d === "string" ? new Date(d) : new Date(d);
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  };

  // Robust API URL builder: resource (path) and optional qs (object)
  apiUrl = (resource, qs = {}) => {
    // prefer explicit apiUrl prop (useful for testing); else env; else fallback to /api
    let base = (this.props.apiBase || process.env.REACT_APP_API || "").replace(
      /\/+$/,
      ""
    );
    if (!base) {
      // fallback to same origin /api
      base = `${window.location.origin.replace(/\/+$/, "")}/api`;
    }

    const path = resource.replace(/^\/+/, "");
    // ensure qs is object
    const query = { ...(qs || {}) };

    // ensure storeId included unless explicitly provided
    try {
      const storeId = this.context?.storeId;
      if (storeId && !query.storeId) query.storeId = storeId;
    } catch (e) {
      // ignore
    }

    const searchParams = new URLSearchParams();
    Object.keys(query).forEach((k) => {
      if (query[k] !== undefined && query[k] !== null) {
        searchParams.append(k, String(query[k]));
      }
    });

    const q = searchParams.toString();
    return q ? `${base}/${path}?${q}` : `${base}/${path}`;
  };

  fetchWithAuth = async (url, opts = {}) => {
    const headers = { ...(opts.headers || {}) };
    if (
      opts.body &&
      !(opts.body instanceof FormData) &&
      !headers["Content-Type"]
    )
      headers["Content-Type"] = "application/json";
    const token = this.context?.authToken;
    if (token) headers["Authorization"] = `Bearer ${token}`;

    // merge headers into opts (do not clobber opts.headers if set)
    const fetchOpts = { ...opts, headers };
    // for debugging: print url
    // console.debug("fetchWithAuth", url, fetchOpts);
    return fetch(url, fetchOpts);
  };

  componentDidMount() {
    this.loadCalendarData();
  }

  loadCalendarData = async () => {
    const { from, days } = this.state;
    const storeId = this.context?.storeId;
    if (!storeId) {
      this.setState({ error: "StoreId missing in context (CartContext)." });
      return;
    }

    this.setState({ loading: true, error: null });
    try {
      const to = new Date(from);
      to.setDate(from.getDate() + days);

      const url = this.apiUrl("HotelReservationCalendar", {
        from: this.fmtDate(from),
        to: this.fmtDate(to),
      });

      const res = await this.fetchWithAuth(url);
      if (!res.ok) {
        const txt = await safeText(res);
        const msg = `API error: ${res.status} ${res.statusText} - ${txt}`;
        console.error(msg, { url });
        throw new Error(msg);
      }
      const data = await res.json();
      this.computeRoomsAndOccupancy(data || []);
    } catch (err) {
      console.error("loadCalendarData failed", err);
      this.setState({
        error: err.message || "Failed to load calendar",
      });
    } finally {
      this.setState({ loading: false });
    }

    async function safeText(r) {
      try {
        return await r.text();
      } catch (e) {
        return r.statusText || `HTTP ${r.status}`;
      }
    }
  };

  computeRoomsAndOccupancy = (entries) => {
    // Expect entries array with shape matching CalendarEntryDto from server.
    // Server may return fields in camelCase: { reservationId, reservationRoomId, roomId, roomNumber, date, ... }
    const roomMap = {};
    entries.forEach((e) => {
      // try multiple possible field names for roomNumber / date
      const rid = e.roomId || e.room?.roomId || e.roomId;
      const rnum =
        e.roomNumber || (e.room && e.room.roomNumber) || "(unassigned)";
      if (!rid) return;
      roomMap[rid] = rnum;
    });

    const rooms = Object.entries(roomMap).map(([roomId, roomNumber]) => ({
      roomId,
      roomNumber,
    }));

    const byDate = {};
    entries.forEach((e) => {
      // handle both 'date' or 'Date' or nested date strings
      const rawDate = e.date || e.Date || (e.Date && e.Date) || null;
      if (!rawDate) return;
      const date = String(rawDate).split("T")[0];
      byDate[date] = (byDate[date] || 0) + 1;
    });

    const totalRooms = rooms.length || 1;
    const occupancyStats = Object.entries(byDate).map(([date, count]) => ({
      date,
      occupancy: Math.round((count / totalRooms) * 100),
    }));

    this.setState({ entries, rooms, occupancyStats });
  };

  handleDragStart = (entry) => {
    this.setState({ dragItem: entry });
  };

  handleDrop = async (room, date) => {
    const { dragItem } = this.state;
    if (!dragItem) return;

    if (
      !window.confirm(
        `Move reservation ${
          dragItem.reservationNo || dragItem.reservationId
        } to ${room.roomNumber} on ${date}?`
      )
    )
      return;

    // Build payload with ISO yyyy-mm-dd dates (no time)
    const newFrom = date;
    // by default move for 1 night; if you want to preserve original nights use calculation below:
    const nights = dragItem.nights || 1;
    const newToDate = new Date(newFrom);
    newToDate.setDate(newToDate.getDate() + Math.max(1, nights));

    const url = this.apiUrl(
      `HotelReservationCalendar/${dragItem.reservationId}/move`
    );
    const payload = {
      reservationRoomId: dragItem.reservationRoomId,
      newRoomId: room.roomId,
      newFromDate: this.fmtDate(newFrom),
      newToDate: this.fmtDate(newToDate),
    };

    try {
      const res = await this.fetchWithAuth(url, {
        method: "POST",
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const txt = await (async () => {
          try {
            return await res.text();
          } catch {
            return res.statusText;
          }
        })();
        throw new Error(`${res.status} ${res.statusText} - ${txt}`);
      }
      this.setState({ dragItem: null });
      await this.loadCalendarData();
      if (this.props.onMoveSuccess) this.props.onMoveSuccess();
    } catch (err) {
      console.error("Move failed", err);
      alert(`Move failed: ${err.message}`);
    }
  };

  renderCell = (room, date) => {
    const { entries } = this.state;
    const dateStr = this.fmtDate(date);

    const list = entries.filter((e) => {
      const rawDate = e.date || e.Date;
      const d = rawDate ? String(rawDate).split("T")[0] : null;
      const rId = e.roomId || (e.room && e.room.roomId) || null;
      return rId === room.roomId && d === dateStr;
    });

    const isDropTarget = this.state.dragItem && list.length === 0;

    // map of colors per status (fallback blue)
    const statusColorMap = {
      CONFIRMED: "#0d6efd",
      CHECKEDIN: "#198754",
      CHECKEDOUT: "#6c757d",
      CANCELLED: "#dc3545",
    };

    return (
      <td
        key={`${room.roomId}-${dateStr}`}
        className={`align-middle text-center ${
          isDropTarget ? "table-success" : ""
        }`}
        onDragOver={(e) => e.preventDefault()}
        onDrop={() => this.handleDrop(room, dateStr)}
      >
        {list.length === 0
          ? "-"
          : list.map((e) => {
              const status = (e.statusCode || e.status || "").toUpperCase();
              const bg = statusColorMap[status] || "#0d6efd";
              const label =
                e.reservationNo ||
                (e.guestName ? `Guest: ${e.guestName}` : e.reservationId);

              return (
                <div
                  key={e.reservationRoomId || e.reservationId}
                  draggable
                  onDragStart={() => this.handleDragStart(e)}
                  title={`${label}\nStatus: ${status}`}
                  style={{
                    backgroundColor: bg,
                    color: "white",
                    borderRadius: "6px",
                    padding: "3px 6px",
                    marginBottom: "4px",
                    boxShadow: "0 1px 2px rgba(0,0,0,0.2)",
                    cursor: "move",
                    fontSize: "0.8rem",
                  }}
                >
                  {label}
                </div>
              );
            })}
      </td>
    );
  };

  render() {
    const { from, days, rooms, loading, error, occupancyStats } = this.state;

    const dates = Array.from({ length: days }, (_, i) => {
      const d = new Date(from);
      d.setDate(from.getDate() + i);
      d.setHours(0, 0, 0, 0);
      return d;
    });

    return (
      <div className="container mt-3">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5>Room Booking Calendar</h5>
          <div>
            <Button
              size="sm"
              variant="outline-secondary"
              onClick={() =>
                this.setState(
                  (s) => ({
                    from: new Date(s.from.setDate(s.from.getDate() - s.days)),
                  }),
                  this.loadCalendarData
                )
              }
            >
              &lt; Prev
            </Button>{" "}
            <Button
              size="sm"
              variant="outline-secondary"
              onClick={() =>
                this.setState(
                  (s) => ({
                    from: new Date(s.from.setDate(s.from.getDate() + s.days)),
                  }),
                  this.loadCalendarData
                )
              }
            >
              Next &gt;
            </Button>{" "}
            <Button size="sm" onClick={this.loadCalendarData}>
              Refresh
            </Button>
          </div>
        </div>

        {error && <Alert variant="danger">{error}</Alert>}
        {loading ? (
          <div className="d-flex align-items-center">
            <Spinner animation="border" size="sm" className="me-2" /> Loading
            calendar...
          </div>
        ) : (
          <>
            {occupancyStats && occupancyStats.length > 0 && (
              <div className="mb-2">
                <strong>Occupancy:</strong>{" "}
                {occupancyStats.map((o) => (
                  <span key={o.date} className="me-2">
                    {o.date}: {o.occupancy}%
                  </span>
                ))}
              </div>
            )}

            <Table bordered hover responsive size="sm">
              <thead>
                <tr>
                  <th>Room</th>
                  {dates.map((d) => (
                    <th key={d.toISOString()}>{this.fmtDate(d)}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rooms.length === 0 ? (
                  <tr>
                    <td colSpan={dates.length + 1} className="text-center">
                      No rooms / data
                    </td>
                  </tr>
                ) : (
                  rooms.map((room) => (
                    <tr key={room.roomId}>
                      <td>
                        <strong>{room.roomNumber}</strong>
                      </td>
                      {dates.map((d) => this.renderCell(room, d))}
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </>
        )}
      </div>
    );
  }
}
